import {
	CheckOutlined,
	PlayCircleOutlined,
	SoundOutlined,
	UnorderedListOutlined,
} from "@ant-design/icons";
import BodhiCategoryCard from "../BodhiCategoryCard";
import "./BodhiBrowseTile.css";

const TYPE_LABELS = {
	genre: "Genre",
	collection: "Collection",
	playlist: "Playlist",
	track: "Track",
};

const TYPE_ICONS = {
	genre: <SoundOutlined />,
	collection: <PlayCircleOutlined />,
	playlist: <UnorderedListOutlined />,
	track: <SoundOutlined />,
};

// Collections read as circles; the other types stay square.
const ROUND_TYPES = new Set(["collection"]);

/** Keep the first `max` unique, non-empty cover URLs. */
export const dedupeCovers = (urls, max = 4) => {
	const seen = new Set();
	const out = [];
	for (const url of urls || []) {
		if (!url || seen.has(url)) continue;
		seen.add(url);
		out.push(url);
		if (out.length >= max) break;
	}
	return out;
};

/**
 * Covers for a playlist mosaic, in the same order as BodhiPlaylistCard and the
 * mobile PlaylistCover: an explicit `covers` array, then the playlist's own
 * artwork, then the artwork of its media's albums.
 */
export const resolvePlaylistCovers = (playlist, explicit) => {
	const fromExplicit = dedupeCovers(explicit);
	if (fromExplicit.length > 0) return fromExplicit;
	const own = playlist?.cover_image_url || playlist?.background_image_url;
	if (own?.trim?.()) return [own];
	return dedupeCovers(
		(playlist?.media || []).map((item) => {
			const album = item?.media_details?.album_details;
			return album?.cover_image_url || album?.background_image_url;
		}),
	);
};

/**
 * One tile for anything browsable, shaped so its type reads at a glance: a
 * gradient block for a genre, a circle for a collection, a mosaic for a
 * playlist, a plain square for a track. The name sits inside the artwork, an
 * optional count badge rides the top and the type pill overhangs the bottom.
 *
 * Fills its grid cell by default; pass `size` (px) for a fixed-width row.
 *
 * @param {"genre"|"collection"|"playlist"|"track"} type - drives shape and pill
 * @param {string} name - shown inside the artwork
 * @param {string} [cover] - artwork for a genre, collection or track
 * @param {string[]} [covers] - mosaic artwork for a playlist
 * @param {object} [playlist] - playlist to resolve mosaic covers from
 * @param {number} [count] - badge number; omit to hide the badge
 * @param {string} [countLabel] - overrides the derived "N tracks" wording
 */
const BodhiBrowseTile = ({
	type = "track",
	name = "",
	cover = null,
	covers = null,
	playlist = null,
	count = null,
	countLabel = null,
	typeLabel = null,
	showTitle = true,
	showTypePill = true,
	selected = false,
	onClick,
	size = null,
	className = "",
	style,
	...rest
}) => {
	const round = ROUND_TYPES.has(type);
	const shape = round ? " is-round" : "";
	const label = typeLabel ?? TYPE_LABELS[type] ?? TYPE_LABELS.track;

	// Genres count the collections filed under them; the rest count tracks.
	const unit = type === "genre" ? "collection" : "track";
	const badge =
		countLabel ??
		(typeof count === "number"
			? `${count} ${unit}${count === 1 ? "" : "s"}`
			: null);

	const art = () => {
		// A genre's gradient card already draws its own name inside the artwork.
		if (type === "genre") {
			return (
				<BodhiCategoryCard
					as="span"
					category={{ name, cover_image_url: cover }}
					size="sm"
					className="bodhi-bt-genre"
				/>
			);
		}
		const artCovers =
			type === "playlist"
				? resolvePlaylistCovers(playlist, covers)
				: dedupeCovers([cover]);
		if (artCovers.length === 0) {
			return (
				<span className={`bodhi-bt-art bodhi-bt-empty${shape}`}>
					{TYPE_ICONS[type] || TYPE_ICONS.track}
				</span>
			);
		}
		if (artCovers.length === 1) {
			return (
				<img
					className={`bodhi-bt-art${shape}`}
					src={artCovers[0]}
					alt=""
					loading="lazy"
				/>
			);
		}
		return (
			<span
				className={`bodhi-bt-art bodhi-bt-mosaic bodhi-bt-mosaic-${artCovers.length}`}
			>
				{artCovers.map((url) => (
					<img key={url} src={url} alt="" loading="lazy" />
				))}
			</span>
		);
	};

	const cls = ["bodhi-bt", selected && "is-selected", className]
		.filter(Boolean)
		.join(" ");

	return (
		<button
			type="button"
			className={cls}
			style={size ? { width: size, flex: "0 0 auto", ...style } : style}
			onClick={onClick}
			title={name}
			aria-pressed={onClick ? selected : undefined}
			{...rest}
		>
			<span className="bodhi-bt-frame">
				{art()}
				{showTitle && type !== "genre" && (
					<span className={`bodhi-bt-title${shape}`}>
						<span className="bodhi-bt-title-text">{name}</span>
					</span>
				)}
				{selected && (
					<span className="bodhi-bt-check">
						<CheckOutlined />
					</span>
				)}
				{badge && <span className={`bodhi-bt-badge${shape}`}>{badge}</span>}
				{showTypePill && <span className="bodhi-bt-pill">{label}</span>}
			</span>
		</button>
	);
};

export default BodhiBrowseTile;
